import NiceModal, { useModal } from '@ebay/nice-modal-react';
import { Grid, Group, Modal, Text, ActionIcon } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';

import { useAddManagerPage } from '../api/addManagerPage';
import { useChangeManagerPagesOrder } from '../api/changeManagerPagesOrder';
import { useGetManagers } from '../api/getManagers';
import { useRemoveManagerPage } from '../api/removeManagerPage';
import { managerTypes } from '../types';

import { PagesSelect } from '@/components';
import { useDragAndDrop } from '@/hooks/useDragAndDrop';
import { Departments } from '@/types';

type Params = {
  manager: managerTypes.Manager;
  department: Departments;
};

type PageParams = {
  page: managerTypes.Manager['pages'][0];
  manager: managerTypes.Manager;
};

const Page = ({ page, manager }: PageParams) => {
  const removePage = useRemoveManagerPage();

  return (
    <Group>
      <ActionIcon
        loading={removePage.isLoading}
        variant="filled"
        onClick={() => removePage.mutate({ managerId: manager._id, pageId: page._id })}
      >
        <IconTrash />
      </ActionIcon>
      <Text size="sm">{page.name}</Text>
    </Group>
  );
};

export const PagesModal = NiceModal.create(({ manager, department }: Params) => {
  const modal = useModal();
  const addPage = useAddManagerPage();
  const changePagesOrder = useChangeManagerPagesOrder();
  const { onDragStart, onDragOver, onDragLeave, onDragDrop } = useDragAndDrop();

  const handleDrop = (startIndex: number, dropIndex: number) => {
    if (dropIndex !== startIndex) {
      const newState = [...manager.pages];
      newState.splice(dropIndex, 0, newState.splice(startIndex, 1)[0]);
      const pageIds = newState.map((page) => page._id);
      changePagesOrder.mutate({ managerId: manager._id, pageIds });
    }
  };

  const getManagers = useGetManagers({
    department,
    config: {
      onSuccess: ({ managers }) => {
        modal.show({ manager: managers.find((m) => m._id === manager._id), department });
      },
    },
  });

  return (
    <Modal
      centered
      overlayProps={{ opacity: 0.4, blur: 1.5 }}
      opened={modal.visible}
      onClose={() => modal.remove()}
      title={`${manager.username} pages`}
    >
      <Grid columns={1} my="md">
        {manager.pages.map((page, index) => (
          <Grid.Col
            span={1}
            key={page._id}
            draggable={true}
            onDragStart={() => onDragStart(index)}
            onDragOver={(e) => onDragOver(e)}
            onDrop={(e) => onDragDrop(e, (startIndex) => handleDrop(startIndex, index))}
            onDragLeave={(e) => onDragLeave(e)}
          >
            <Page key={page._id} page={page} manager={manager} />
          </Grid.Col>
        ))}
      </Grid>

      <PagesSelect
        value=""
        onSelect={(pageId) => addPage.mutate({ managerId: manager._id, pageId })}
        isLoading={addPage.isLoading || getManagers.isFetching || changePagesOrder.isLoading}
        disabled={manager.pages.map((page) => page._id)}
      />
    </Modal>
  );
});
